import React, { useState, useEffect, useMemo } from "react";
import { getServiceUsers } from "../../services/users.js";
import UserProfileSummary from "./UserProfileSummary.js";
import CreateServiceUser from "../modals/CreateServiceUserModal.js";
import ReusableTable from "../ReusableTable.js";
import { useSearchParams } from "react-router-dom";
import { dateFormat } from "../../utils/functions.js";

export const ServiceUsers = () => {
  const [usersList, setUsers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  // Get initial state from URL (defaults if not set)
  const pageIndex = useMemo(() => {
    const pageParam = searchParams.get("page");
    const page = parseInt(pageParam, 10);
    return isNaN(page) ? 0 : Math.max(0, page);
  }, [searchParams]);
  const expandedRows = JSON.parse(searchParams.get("expanded") || "{}");

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const { data } = await getServiceUsers();
      setUsers(data);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const columns = useMemo(
    () => [
      { accessorKey: "firstName", header: "First Name" },
      { accessorKey: "lastName", header: "Last Name" },
      { accessorKey: "telephoneNumber", header: "Phone Number" },
      { accessorKey: "emailAddress", header: "Email" },
      { accessorKey: "ethnicity", header: "Ethnicity" },
      { accessorKey: "gender", header: "Gender" },
      {
        accessorKey: "startDate",
        header: "Start Date",
        cell: ({ row }) =>
          dateFormat(new Date(row.original.startDate), "dd/MM/yyyy"),
      },
    ],
    []
  );

  const updatePage = (newPageIndex) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("page", newPageIndex);
      return params;
    });
  };

  const updateExpandedRows = (rowId, isExpanded) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      const expanded = JSON.parse(params.get("expanded") || "{}");
      if (isExpanded) expanded[rowId] = true;
      else delete expanded[rowId];
      params.set("expanded", JSON.stringify(expanded));
      return params;
    });
  };

  return (
    <>
      <div className="user__top">
        <button
          className="btn btn__primary"
          onClick={() => setIsModalOpen(true)}
        >
          + Add service user
        </button>
      </div>

      <ReusableTable
        data={usersList}
        columns={columns}
        isLoading={isLoading}
        enableMasterDetails={true}
        renderDetails={(user) => (
          <UserProfileSummary
            user={user}
            detailsUrl="/service-user/"
            userType="service_user"
          />
        )}
        pageIndex={pageIndex}
        onPageChange={updatePage}
        expandedRows={expandedRows}
        onRowExpandChange={updateExpandedRows}
      />

      {isModalOpen && (
        <CreateServiceUser
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          reloadUsers={fetchUsers}
        />
      )}
    </>
  );
};
